import { useInfiniteQuery, useQuery, type UseQueryResult } from '@tanstack/react-query';
import {
  PEOPLE_SEARCH_MIN,
  type AskHrInput,
  type AssignMailInput,
  type ComposeMessageRequest,
  type ComposePreview,
  type CreateTaskRequest,
  type CreateTemplateInput,
  type InboxMailDetail,
  type InboxView,
  type LogActivityRequest,
  type MessageSent,
  type MyTasksView,
  type PersonMatch,
  type SaveTemplateInput,
  type TaskAssignee,
  type TaskListScope,
  type TaskRow,
  type TemplatePreview,
  type TemplatePreviewRequest,
  type TemplateRow,
  type TemplateVersionRow,
  type TimelineFilter,
  type TimelineView,
} from '@nest-recruit/shared';
import { apiGet, apiPost, apiPut, type ApiError } from '../../../lib/api-client';

/**
 * S26 — COMM-01–04, ADMIN-09 and the inbound queue. The server's realtime hints name
 * `timeline`, `tasks` and `inbox`, so a reply arriving or a task handed over shows without a reload.
 */

export const TIMELINE_KEY = ['timeline'] as const;
export const TASKS_KEY = ['tasks'] as const;
export const INBOX_KEY = ['inbox'] as const;
export const TEMPLATES_KEY = ['message-templates'] as const;

export type TimelineSubject = { readonly kind: 'application'; readonly id: string } | { readonly kind: 'candidate'; readonly id: string } | { readonly kind: 'job'; readonly id: string };

function timelinePath(subject: TimelineSubject): string {
  if (subject.kind === 'application') return `/applications/${subject.id}/timeline`;
  if (subject.kind === 'candidate') return `/candidates/${subject.id}/timeline`;
  return `/jobs/${subject.id}/activity`;
}

const PAGE = 40;

/** Older pages load on "Show older"; the newest page never blanks while it refreshes. */
export function useTimeline(subject: TimelineSubject, filter: TimelineFilter) {
  return useInfiniteQuery<TimelineView, ApiError>({
    queryKey: [...TIMELINE_KEY, subject.kind, subject.id, filter],
    queryFn: ({ pageParam }) => apiGet<TimelineView>(timelinePath(subject), { query: { type: filter, limit: PAGE, before: typeof pageParam === 'string' ? pageParam : undefined } }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => (last.hasMore ? (last.items[last.items.length - 1]?.at ?? null) : null),
    placeholderData: (previous) => previous,
  });
}

export function useMyTasks(scope: TaskListScope): UseQueryResult<MyTasksView, ApiError> {
  return useQuery({
    queryKey: [...TASKS_KEY, 'mine', scope],
    queryFn: () => apiGet<MyTasksView>('/tasks', { query: { scope } }),
    placeholderData: (previous) => previous,
  });
}

export function useTaskAssignees(isEnabled: boolean): UseQueryResult<readonly TaskAssignee[], ApiError> {
  return useQuery({
    queryKey: [...TASKS_KEY, 'assignees'],
    queryFn: () => apiGet<readonly TaskAssignee[]>('/tasks/assignees'),
    enabled: isEnabled,
    staleTime: 300_000,
  });
}

export function useHmQuestions(requisitionIds: readonly string[]): UseQueryResult<readonly TaskRow[], ApiError> {
  return useQuery({
    queryKey: [...TASKS_KEY, 'questions', requisitionIds.join(',')],
    queryFn: () => apiGet<readonly TaskRow[]>('/hm/questions', { query: { requisitionIds: requisitionIds.join(',') } }),
    enabled: requisitionIds.length > 0,
    placeholderData: (previous) => previous,
  });
}

export function useComposeTemplates(isEnabled: boolean): UseQueryResult<readonly TemplateRow[], ApiError> {
  return useQuery({
    queryKey: [...TEMPLATES_KEY, 'compose'],
    queryFn: () => apiGet<readonly TemplateRow[]>('/messages/templates'),
    enabled: isEnabled,
  });
}

export function useComposePreview(target: { readonly applicationId: string | null; readonly candidateId: string | null }, templateKey: string | null, isEnabled: boolean): UseQueryResult<ComposePreview, ApiError> {
  return useQuery({
    queryKey: ['compose-preview', target.applicationId, target.candidateId, templateKey],
    queryFn: () => apiGet<ComposePreview>('/messages/preview', { query: { applicationId: target.applicationId ?? undefined, candidateId: target.candidateId ?? undefined, templateKey: templateKey ?? undefined } }),
    enabled: isEnabled,
  });
}

export function useAdminTemplates(isEnabled: boolean): UseQueryResult<readonly TemplateRow[], ApiError> {
  return useQuery({
    queryKey: [...TEMPLATES_KEY, 'admin'],
    queryFn: () => apiGet<readonly TemplateRow[]>('/admin/message-templates'),
    enabled: isEnabled,
    placeholderData: (previous) => previous,
  });
}

export function useTemplateVersions(id: string | null): UseQueryResult<readonly TemplateVersionRow[], ApiError> {
  return useQuery({
    queryKey: [...TEMPLATES_KEY, 'versions', id],
    queryFn: () => apiGet<readonly TemplateVersionRow[]>(`/admin/message-templates/${id ?? ''}/versions`),
    enabled: id !== null,
  });
}

/** The mailbox is read every two minutes; an open queue looks again every minute, so new mail appears by itself. */
const INBOX_REFRESH_MS = 60_000;

export function inboxPageKey(page: number): readonly unknown[] {
  return [...INBOX_KEY, 'list', page];
}

/** One page of the waiting emails, with the mailbox line. The page on screen never blanks while the next loads. */
export function useInbox(page: number, isEnabled: boolean): UseQueryResult<InboxView, ApiError> {
  return useQuery({
    queryKey: inboxPageKey(page),
    queryFn: () => apiGet<InboxView>('/admin/inbox', { query: { page } }),
    enabled: isEnabled,
    placeholderData: (previous) => previous,
    refetchInterval: INBOX_REFRESH_MS,
  });
}

/** The whole email and why it waits. Only the opened one is asked for. */
export function useInboxMail(id: string | null): UseQueryResult<InboxMailDetail, ApiError> {
  return useQuery({
    queryKey: [...INBOX_KEY, 'mail', id],
    queryFn: () => apiGet<InboxMailDetail>(`/admin/inbox/${id ?? ''}`),
    enabled: id !== null,
  });
}

/** "File against": the people a search finds, with the applications this viewer may file on. */
export function useInboxPeople(mailId: string | null, query: string): UseQueryResult<readonly PersonMatch[], ApiError> {
  const trimmed = query.trim();
  return useQuery({
    queryKey: [...INBOX_KEY, 'people', mailId, trimmed],
    queryFn: () => apiGet<readonly PersonMatch[]>(`/admin/inbox/${mailId ?? ''}/people`, { query: { q: trimmed } }),
    enabled: mailId !== null && trimmed.length >= PEOPLE_SEARCH_MIN,
    placeholderData: (previous) => previous,
  });
}

/** The template preview's "As …": people and the applications this viewer may read. */
export function usePreviewPeople(query: string, isEnabled: boolean): UseQueryResult<readonly PersonMatch[], ApiError> {
  const trimmed = query.trim();
  return useQuery({
    queryKey: [...TEMPLATES_KEY, 'preview-people', trimmed],
    queryFn: () => apiGet<readonly PersonMatch[]>('/admin/message-templates/applications', { query: { q: trimmed } }),
    enabled: isEnabled && trimmed.length >= PEOPLE_SEARCH_MIN,
    placeholderData: (previous) => previous,
  });
}

/**
 * The words being written, as the email they make (it reads only; nothing is sent). Keyed by the words themselves, so
 * typing back to an earlier text is answered from the cache; the last email stays on screen while the next renders.
 */
export function useTemplatePreview(input: TemplatePreviewRequest, isEnabled: boolean): UseQueryResult<TemplatePreview, ApiError> {
  return useQuery({
    queryKey: [...TEMPLATES_KEY, 'preview', input.templateId ?? null, input.applicationId ?? null, input.subject ?? '', input.bodyText ?? ''],
    queryFn: () => apiPost<TemplatePreview>('/admin/message-templates/preview', input),
    enabled: isEnabled,
    placeholderData: (previous) => previous,
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
  });
}

export const communicationApi = {
  log: (input: LogActivityRequest) => apiPost<{ readonly activityId: string; readonly taskId: string | null }>('/activities', input),
  send: (input: ComposeMessageRequest) => apiPost<MessageSent>('/messages', input),
  createTask: (input: CreateTaskRequest) => apiPost<TaskRow>('/tasks', input),
  completeTask: (id: string, version: number, answer: string | null) => apiPost<TaskRow>(`/tasks/${id}/complete`, { version, answer }),
  reopenTask: (id: string, version: number) => apiPost<TaskRow>(`/tasks/${id}/reopen`, { version }),
  reassignTask: (id: string, version: number, assigneeUserId: string) => apiPost<TaskRow>(`/tasks/${id}/reassign`, { version, assigneeUserId }),
  askHr: (input: AskHrInput) => apiPost<{ readonly taskId: string }>('/hm/ask-hr', input),
  createTemplate: (input: CreateTemplateInput) => apiPost<TemplateRow>('/admin/message-templates', input),
  saveTemplate: (id: string, input: SaveTemplateInput) => apiPut<TemplateRow>(`/admin/message-templates/${id}`, input),
  switchTemplate: (id: string, version: number, isOn: boolean) => apiPost<TemplateRow>(`/admin/message-templates/${id}/${isOn ? 'switch-on' : 'switch-off'}`, { version }),
  assignMail: (id: string, input: AssignMailInput) => apiPost<{ readonly id: string }>(`/admin/inbox/${id}/assign`, input),
  dismissMail: (id: string) => apiPost<{ readonly id: string }>(`/admin/inbox/${id}/dismiss`, {}),
  restoreMail: (id: string) => apiPost<{ readonly id: string }>(`/admin/inbox/${id}/restore`, {}),
  checkInbox: () => apiPost<{ readonly summary: string }>('/admin/inbox/check', {}),
};
